import { useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import Moveable from "react-moveable";
import JSZip from "jszip";
import PQueue from "p-queue";
import {
  composeOutput,
  loadImageFromFile,
  loadImageFromPath,
  type ExportFormat,
  type LayerGeometry,
  type Placement,
  type Point
} from "./lib/composer";
import { pickDirectory, pickOutputDirectory, saveBytes, walkFiles, writeBytesToDirectory } from "./lib/fileApi";

const SUPPORTED_IMAGE_EXTENSIONS = new Set([".png", ".jpg", ".jpeg", ".webp", ".bmp", ".gif", ".avif", ".jfif", ".tif", ".tiff"]);

type LayerSourceMode = "fixed" | "batch";
type SaveMode = "zip" | "folder";

interface OverlayLayer {
  id: string;
  name: string;
  image: HTMLImageElement;
  visible: boolean;
  opacity: number;
  sourceMode: LayerSourceMode;
  geometry: LayerGeometry;
  initialPlacement: Placement;
  editorTransform: string | null;
}

function getExtension(path: string) {
  const normalized = path.replace(/\\/g, "/");
  const dotIndex = normalized.lastIndexOf(".");
  if (dotIndex < 0) {
    return "";
  }
  return normalized.slice(dotIndex).toLowerCase();
}

function isSupportedImagePath(path: string) {
  return SUPPORTED_IMAGE_EXTENSIONS.has(getExtension(path));
}

function fileNameFromPath(input: string) {
  const parts = input.split(/[\\/]/);
  return parts[parts.length - 1] ?? input;
}

function baseNameFromPath(input: string) {
  const fileName = fileNameFromPath(input);
  return fileName.replace(/\.[^.]+$/, "");
}

function ts() {
  return new Date().toISOString().replace(/[:.]/g, "-");
}

function sanitizeFileName(input: string) {
  return input.replace(/[<>:"/\\|?*\x00-\x1F]/g, "_");
}

function normalizeForPath(input: string) {
  return input.replace(/\\/g, "/").replace(/\/+/g, "/");
}

function relativeDirectoryPath(filePath: string, rootDir: string) {
  const normalizedFile = normalizeForPath(filePath);
  const normalizedRoot = normalizeForPath(rootDir).replace(/\/$/, "");
  const prefix = `${normalizedRoot}/`;
  if (!normalizedFile.startsWith(prefix)) {
    return "";
  }
  const relative = normalizedFile.slice(prefix.length);
  const parts = relative.split("/");
  parts.pop();
  return parts.join("/");
}

function extensionForFormat(format: ExportFormat) {
  if (format === "jpeg") {
    return "jpg";
  }
  if (format === "webp") {
    return "webp";
  }
  return "png";
}

function quadToLog(quad: [Point, Point, Point, Point]) {
  return quad
    .map((pt, idx) => `p${idx}(${Math.round(pt.x)},${Math.round(pt.y)})`)
    .join(" ");
}

function clampPlacement(placement: Placement, maxWidth: number, maxHeight: number): Placement {
  const width = Math.max(20, Math.min(maxWidth, placement.width));
  const height = Math.max(20, Math.min(maxHeight, placement.height));
  const x = Math.max(-maxWidth, Math.min(maxWidth * 2, placement.x));
  const y = Math.max(-maxHeight, Math.min(maxHeight * 2, placement.y));
  return { ...placement, width, height, x, y };
}

function rotatePoint(point: Point, origin: Point, rad: number): Point {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  return {
    x: origin.x + dx * Math.cos(rad) - dy * Math.sin(rad),
    y: origin.y + dx * Math.sin(rad) + dy * Math.cos(rad)
  };
}

function placementToQuad(placement: Placement): [Point, Point, Point, Point] {
  const origin = { x: placement.x, y: placement.y };
  const rad = (placement.rotation * Math.PI) / 180;
  const p0 = origin;
  const p1 = { x: placement.x + placement.width, y: placement.y };
  const p2 = { x: placement.x + placement.width, y: placement.y + placement.height };
  const p3 = { x: placement.x, y: placement.y + placement.height };
  return [rotatePoint(p0, origin, rad), rotatePoint(p1, origin, rad), rotatePoint(p2, origin, rad), rotatePoint(p3, origin, rad)];
}

const IDENTITY_MATRIX3D = "matrix3d(1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1)";

function px(input: string | null | undefined, fallback = 0) {
  if (!input) {
    return fallback;
  }
  const value = Number.parseFloat(input);
  return Number.isFinite(value) ? value : fallback;
}

function rounded(value: number) {
  return Math.round(value * 100) / 100;
}

function isBlobUrl(url: string | undefined | null) {
  return Boolean(url && url.startsWith("blob:"));
}

function parseCssMatrix(input: string | null | undefined) {
  const value = input && input !== "none" ? input : IDENTITY_MATRIX3D;
  try {
    return new DOMMatrixReadOnly(value);
  } catch {
    return new DOMMatrixReadOnly(IDENTITY_MATRIX3D);
  }
}

function clonePlacement(placement: Placement): Placement {
  return {
    x: placement.x,
    y: placement.y,
    width: placement.width,
    height: placement.height,
    rotation: placement.rotation
  };
}

function boundsFromQuad(quad: [Point, Point, Point, Point]) {
  const xs = quad.map((pt) => pt.x);
  const ys = quad.map((pt) => pt.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    minX,
    minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY)
  };
}

function hasWarpShape(layer: OverlayLayer) {
  return Boolean(layer.geometry.quad && layer.editorTransform);
}

function StepBadge({ index, title, done }: { index: number; title: string; done: boolean }) {
  return (
    <div className={`step-badge ${done ? "done" : ""}`}>
      <span>{index}</span>
      <strong>{title}</strong>
    </div>
  );
}

function App() {
  const [templateImage, setTemplateImage] = useState<HTMLImageElement | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [layers, setLayers] = useState<OverlayLayer[]>([]);
  const [activeLayerId, setActiveLayerId] = useState<string | null>(null);

  const [recordsDir, setRecordsDir] = useState("");
  const [allImagePaths, setAllImagePaths] = useState<string[]>([]);
  const [selectedMap, setSelectedMap] = useState<Record<string, boolean>>({});

  const [layoutDirty, setLayoutDirty] = useState(true);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewSourceName, setPreviewSourceName] = useState("");
  const [saveMode, setSaveMode] = useState<SaveMode>("zip");
  const [exportFormat, setExportFormat] = useState<ExportFormat>("png");
  const [exportQuality, setExportQuality] = useState(0.92);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [pickingRecordsDir, setPickingRecordsDir] = useState(false);

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  const queueRef = useRef<PQueue | null>(null);
  const cancelledRef = useRef(false);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const layerElementRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeTarget, setActiveTarget] = useState<HTMLDivElement | null>(null);
  const activeBlobUrlsRef = useRef<Set<string>>(new Set());
  const moveableDragMovedRef = useRef(0);
  const moveableDragTickRef = useRef(0);
  const moveableWarpTickRef = useRef(0);

  const activeLayer = useMemo(() => layers.find((layer) => layer.id === activeLayerId) ?? null, [layers, activeLayerId]);
  const hasBatchLayer = useMemo(() => layers.some((layer) => layer.sourceMode === "batch"), [layers]);
  const selectedPaths = useMemo(() => allImagePaths.filter((path) => selectedMap[path]), [allImagePaths, selectedMap]);

  const stageScale = useMemo(() => {
    if (!templateImage) {
      return 1;
    }
    const widthScale = 760 / templateImage.naturalWidth;
    const heightScale = 500 / templateImage.naturalHeight;
    return Math.min(1, widthScale, heightScale);
  }, [templateImage]);
  const stageWidth = templateImage ? Math.round(templateImage.naturalWidth * stageScale) : 760;
  const stageHeight = templateImage ? Math.round(templateImage.naturalHeight * stageScale) : 500;

  const canPreview = Boolean(templateImage && layers.length > 0 && (!hasBatchLayer || selectedPaths.length > 0) && !running);
  const canBatch = Boolean(
    templateImage && layers.length > 0 && (!hasBatchLayer || selectedPaths.length > 0) && previewUrl && !layoutDirty && !running
  );

  const stepDone = {
    step1: Boolean(templateImage),
    step2: layers.length > 0,
    step3: Boolean(recordsDir) && selectedPaths.length > 0,
    step4: Boolean(previewUrl && !layoutDirty)
  };

  function trace(line: string) {
    console.info("[BatchComposer]", line);
  }

  function appendLog(line: string) {
    const stamped = `${new Date().toISOString()} ${line}`;
    trace(stamped);
    setLogs((prev) => [...prev, stamped]);
  }

  function clearPreview() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
      setPreviewSourceName("");
      setPreviewModalOpen(false);
    }
  }

  function markDirty(clearPreviewNow = true) {
    setLayoutDirty(true);
    if (clearPreviewNow) {
      clearPreview();
    }
  }

  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  useEffect(() => {
    const next = new Set<string>();
    if (templateImage?.src && isBlobUrl(templateImage.src)) {
      next.add(templateImage.src);
    }
    for (const layer of layers) {
      if (layer.image.src && isBlobUrl(layer.image.src)) {
        next.add(layer.image.src);
      }
    }
    const previous = activeBlobUrlsRef.current;
    for (const url of previous) {
      if (!next.has(url)) {
        URL.revokeObjectURL(url);
      }
    }
    activeBlobUrlsRef.current = next;
  }, [templateImage, layers]);

  useEffect(
    () => () => {
      for (const url of activeBlobUrlsRef.current) {
        URL.revokeObjectURL(url);
      }
      activeBlobUrlsRef.current.clear();
    },
    []
  );

  useEffect(() => {
    if (!previewModalOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPreviewModalOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [previewModalOpen]);

  useEffect(() => {
    if (activeLayerId && layers.some((item) => item.id === activeLayerId)) {
      return;
    }
    setActiveLayerId(layers[0]?.id ?? null);
  }, [layers, activeLayerId]);

  useEffect(() => {
    if (!activeLayerId) {
      setActiveTarget(null);
      return;
    }
    setActiveTarget(layerElementRefs.current[activeLayerId] ?? null);
  }, [activeLayerId, layers]);

  const templateDrop = useDropzone({
    accept: { "image/*": [] },
    maxFiles: 1,
    onDrop: async (files) => {
      const file = files[0];
      if (!file) {
        return;
      }
      const image = await loadImageFromFile(file);
      setTemplateImage(image);
      setTemplateName(file.name);
      setLayers([]);
      setActiveLayerId(null);
      setLayoutDirty(true);
      clearPreview();
      appendLog(`模板图已上传: ${file.name}`);
      appendLog(`模板尺寸: ${image.naturalWidth}x${image.naturalHeight}`);
    }
  });

  const layerDrop = useDropzone({
    accept: { "image/*": [] },
    onDrop: async (files) => {
      if (!templateImage || files.length === 0) {
        return;
      }
      const loaded: OverlayLayer[] = [];
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const image = await loadImageFromFile(file);
        const baseWidth = Math.round(templateImage.naturalWidth * 0.26);
        const scale = baseWidth / image.naturalWidth;
        const height = Math.round(image.naturalHeight * scale);
        const x = Math.round(templateImage.naturalWidth * 0.16) + i * 20;
        const y = Math.round(templateImage.naturalHeight * 0.2) + i * 20;
        loaded.push({
          id: crypto.randomUUID(),
          name: file.name,
          image,
          visible: true,
          opacity: 1,
          sourceMode: layers.length + i === 0 ? "batch" : "fixed",
          geometry: {
            placement: { x, y, width: baseWidth, height, rotation: 0 },
            perspectiveEnabled: false,
            quad: null
          },
          initialPlacement: { x, y, width: baseWidth, height, rotation: 0 },
          editorTransform: null
        });
      }
      setLayers((prev) => [...prev, ...loaded]);
      setActiveLayerId(loaded[0]?.id ?? null);
      markDirty();
      appendLog(`新增图层: ${loaded.length} 个`);
      appendLog(
        loaded
          .map((layer) => `${layer.name}(${layer.geometry.placement.width}x${layer.geometry.placement.height})`)
          .join(" | ")
      );
    }
  });

  async function onPickRecordsFolder() {
    if (pickingRecordsDir) {
      appendLog("素材目录选择中，忽略重复触发。");
      return;
    }
    setPickingRecordsDir(true);
    try {
      const dir = await pickDirectory();
      if (!dir) {
        appendLog("素材目录选择已取消。");
        return;
      }
      const files = await walkFiles(dir);
      const imageFiles = files.filter((filePath) => isSupportedImagePath(filePath));
      const unsupported = files.filter((filePath) => !isSupportedImagePath(filePath));
      const unsupportedExts = new Set<string>();
      for (const file of unsupported) {
        const ext = getExtension(file);
        if (ext) {
          unsupportedExts.add(ext);
        }
      }
      setRecordsDir(dir);
      setAllImagePaths(imageFiles);
      const nextMap: Record<string, boolean> = {};
      for (const path of imageFiles) {
        nextMap[path] = true;
      }
      setSelectedMap(nextMap);
      markDirty();
      appendLog(`素材目录已加载: ${imageFiles.length} 张图片`);
      if (unsupportedExts.size > 0) {
        appendLog(`未纳入格式: ${Array.from(unsupportedExts).join(", ")}`);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "unknown error";
      appendLog(`素材目录读取失败: ${msg}`);
    } finally {
      setPickingRecordsDir(false);
    }
  }

  function selectAllFiles(checked: boolean) {
    const nextMap: Record<string, boolean> = {};
    for (const path of allImagePaths) {
      nextMap[path] = checked;
    }
    setSelectedMap(nextMap);
    markDirty();
  }

  function togglePath(path: string, checked: boolean) {
    setSelectedMap((prev) => ({ ...prev, [path]: checked }));
    markDirty();
  }

  function updateLayer(
    layerId: string,
    updater: (layer: OverlayLayer) => OverlayLayer,
    options?: {
      clearPreviewNow?: boolean;
    }
  ) {
    setLayers((prev) => prev.map((layer) => (layer.id === layerId ? updater(layer) : layer)));
    markDirty(options?.clearPreviewNow ?? true);
  }

  function removeLayer(layerId: string) {
    setLayers((prev) => prev.filter((layer) => layer.id !== layerId));
    setActiveLayerId((prev) => (prev === layerId ? null : prev));
    delete layerElementRefs.current[layerId];
    markDirty();
    appendLog(`删除图层: ${layerId}`);
  }

  function setLayerSourceMode(layerId: string, mode: LayerSourceMode) {
    setLayers((prev) =>
      prev.map((layer) => {
        if (layer.id === layerId) {
          return { ...layer, sourceMode: mode };
        }
        if (mode === "batch" && layer.sourceMode === "batch") {
          return { ...layer, sourceMode: "fixed" };
        }
        return layer;
      })
    );
    markDirty();
    appendLog(`图层来源切换: ${layerId} -> ${mode}`);
  }

  function togglePerspective(layerId: string, enabled: boolean) {
    let liveQuad: [Point, Point, Point, Point] | null = null;
    let liveTransform: string | null = null;
    let livePlacement: Placement | null = null;
    if (!enabled && activeTarget && activeTarget.dataset.layerId === layerId) {
      liveQuad = readQuadFromElement(activeTarget);
      liveTransform = activeTarget.style.transform || null;
      if (liveQuad) {
        appendLog(`取消透视前捕获当前四角: ${layerId} ${quadToLog(liveQuad)}`);
      }
      if (templateImage) {
        livePlacement = clampPlacement(
          {
            x: px(activeTarget.style.left) / stageScale,
            y: px(activeTarget.style.top) / stageScale,
            width: px(activeTarget.style.width, activeTarget.offsetWidth) / stageScale,
            height: px(activeTarget.style.height, activeTarget.offsetHeight) / stageScale,
            rotation: 0
          },
          templateImage.naturalWidth,
          templateImage.naturalHeight
        );
      }
    }
    setLayers((prev) =>
      prev.map((layer) => {
        if (layer.id !== layerId) {
          return layer;
        }
        if (!enabled) {
          const sourceQuad = liveQuad ?? layer.geometry.quad;
          const nextPlacement =
            livePlacement ??
            (sourceQuad
              ? (() => {
                  const b = boundsFromQuad(sourceQuad);
                  return {
                    ...layer.geometry.placement,
                    x: b.minX,
                    y: b.minY,
                    width: b.width,
                    height: b.height,
                    rotation: 0
                  };
                })()
              : layer.geometry.placement);
          return {
            ...layer,
            // Disable perspective editing but keep the current transformed shape.
            editorTransform: liveTransform ?? layer.editorTransform ?? IDENTITY_MATRIX3D,
            geometry: {
              ...layer.geometry,
              placement: nextPlacement,
              perspectiveEnabled: false,
              quad: sourceQuad ?? layer.geometry.quad
            }
          };
        }
        return {
          ...layer,
          editorTransform: layer.editorTransform ?? IDENTITY_MATRIX3D,
          geometry: {
            ...layer.geometry,
            perspectiveEnabled: true,
            quad: layer.geometry.quad ?? placementToQuad(layer.geometry.placement)
          }
        };
      })
    );
    markDirty();
    appendLog(`透视模式: ${layerId} -> ${enabled ? "on" : "off"}`);
  }

  function resetPerspective(layerId: string) {
    updateLayer(layerId, (layer) => ({
      ...layer,
      editorTransform: IDENTITY_MATRIX3D,
      geometry: {
        ...layer.geometry,
        perspectiveEnabled: true,
        quad: placementToQuad(layer.geometry.placement)
      }
    }));
    const target = layers.find((layer) => layer.id === layerId);
    if (target) {
      appendLog(`透视重置: ${layerId} ${quadToLog(placementToQuad(target.geometry.placement))}`);
    }
  }

  function commitPerspectiveQuad(
    layerId: string,
    quad: [Point, Point, Point, Point],
    editorTransform?: string,
    placementOverride?: Placement
  ) {
    updateLayer(layerId, (layer) => {
      return {
        ...layer,
        editorTransform: editorTransform ?? layer.editorTransform ?? IDENTITY_MATRIX3D,
        geometry: {
          ...layer.geometry,
          placement: placementOverride ? clonePlacement(placementOverride) : layer.geometry.placement,
          quad
        }
      };
    });
    appendLog(`透视提交: ${layerId} ${quadToLog(quad)}`);
  }

  function setLayerElementRef(layerId: string, element: HTMLDivElement | null) {
    layerElementRefs.current[layerId] = element;
  }

  function readQuadFromElement(target: HTMLDivElement): [Point, Point, Point, Point] | null {
    if (!stageScale) {
      return null;
    }
    const left = px(target.style.left);
    const top = px(target.style.top);
    const width = px(target.style.width, target.offsetWidth);
    const height = px(target.style.height, target.offsetHeight);
    const matrix = parseCssMatrix(target.style.transform);
    const corners: Array<[number, number]> = [
      [0, 0],
      [width, 0],
      [width, height],
      [0, height]
    ];
    const points: Point[] = corners.map(([x, y]) => {
      const transformed = new DOMPoint(x, y, 0, 1).matrixTransform(matrix);
      const w = transformed.w && transformed.w !== 0 ? transformed.w : 1;
      return {
        x: (left + transformed.x / w) / stageScale,
        y: (top + transformed.y / w) / stageScale
      };
    });
    return points as [Point, Point, Point, Point];
  }

  function commitPerspectiveFromElement(layerId: string, target: HTMLDivElement, reason: string) {
    if (!templateImage) {
      return;
    }
    const quad = readQuadFromElement(target);
    if (!quad) {
      appendLog(`透视提交失败(${reason}): ${layerId} 未获取到四角坐标`);
      return;
    }
    const nextPlacement = clampPlacement(
      {
        x: px(target.style.left) / stageScale,
        y: px(target.style.top) / stageScale,
        width: px(target.style.width, target.offsetWidth) / stageScale,
        height: px(target.style.height, target.offsetHeight) / stageScale,
        rotation: 0
      },
      templateImage.naturalWidth,
      templateImage.naturalHeight
    );
    const transform = target.style.transform || IDENTITY_MATRIX3D;
    commitPerspectiveQuad(layerId, quad, transform, nextPlacement);
    appendLog(
      `透视交互完成(${reason}): ${layerId} x=${rounded(nextPlacement.x)} y=${rounded(nextPlacement.y)} w=${rounded(
        nextPlacement.width
      )} h=${rounded(nextPlacement.height)} matrix=${transform.slice(0, 64)}...`
    );
  }

  function resetLayerPlacement(layerId: string) {
    updateLayer(layerId, (layer) => {
      const base = clonePlacement(layer.initialPlacement);
      return {
        ...layer,
        editorTransform: null,
        geometry: {
          ...layer.geometry,
          placement: base,
          perspectiveEnabled: false,
          quad: null
        }
      };
    });
    if (activeLayerId === layerId) {
      setActiveLayerId(null);
      window.setTimeout(() => setActiveLayerId(layerId), 0);
    }
    appendLog(`重置位置: ${layerId}`);
  }

  function commitNormalFromElement(layerId: string, target: HTMLDivElement, reason: string) {
    if (!templateImage) {
      return;
    }
    const layer = layers.find((item) => item.id === layerId);
    const left = px(target.style.left);
    const top = px(target.style.top);
    const width = px(target.style.width, target.offsetWidth);
    const height = px(target.style.height, target.offsetHeight);
    const rotation = px(target.dataset.rotation, layer?.geometry.placement.rotation ?? 0);
    const next = clampPlacement(
      {
        x: left / stageScale,
        y: top / stageScale,
        width: width / stageScale,
        height: height / stageScale,
        rotation
      },
      templateImage.naturalWidth,
      templateImage.naturalHeight
    );
    updateLayer(layerId, (prev) => ({
      ...prev,
      editorTransform: null,
      geometry: {
        ...prev.geometry,
        placement: next
      }
    }));
    appendLog(
      `普通交互完成(${reason}): ${layerId} x=${rounded(next.x)} y=${rounded(next.y)} w=${rounded(next.width)} h=${rounded(
        next.height
      )} r=${rounded(next.rotation)}`
    );
  }

  async function resolveComposedLayers(batchImagePath: string | null) {
    const composed = [];
    for (const layer of layers) {
      if (!layer.visible) {
        continue;
      }
      let image = layer.image;
      if (layer.sourceMode === "batch") {
        if (!batchImagePath) {
          throw new Error("存在批处理图层，但未选择素材图片。");
        }
        image = await loadImageFromPath(batchImagePath);
      }
      composed.push({
        image,
        visible: layer.visible,
        opacity: layer.opacity,
        geometry: layer.geometry
      });
    }
    return composed;
  }

  async function onGenerateFirstPreview() {
    if (!templateImage || layers.length === 0) {
      return;
    }
    try {
      const firstPath = hasBatchLayer ? selectedPaths[0] : null;
      appendLog(`开始生成首张预览: ${firstPath ? fileNameFromPath(firstPath) : "固定图层"} layers=${layers.length}`);
      const composedLayers = await resolveComposedLayers(firstPath ?? null);
      const blob = await composeOutput(templateImage, composedLayers, exportFormat, exportQuality);
      clearPreview();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewSourceName(firstPath ? fileNameFromPath(firstPath) : "固定图层预览");
      setLayoutDirty(false);
      setPreviewModalOpen(true);
      appendLog(`首张预览已生成: ${firstPath ? fileNameFromPath(firstPath) : "固定图层"}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "unknown error";
      appendLog(`首张预览失败: ${msg}`);
    }
  }

  async function onGenerateBatch() {
    if (!templateImage || !canBatch) {
      return;
    }
    setRunning(true);
    setProgress(0);
    cancelledRef.current = false;
    const queue = new PQueue({ concurrency: 2 });
    queueRef.current = queue;
    const zip = new JSZip();
    const ext = extensionForFormat(exportFormat);
    let outputDir: string | null = null;
    let failed = 0;
    let done = 0;

    try {
      appendLog(
        `开始批量导出: mode=${saveMode} format=${exportFormat} quality=${Math.round(exportQuality * 100)}% selected=${
          hasBatchLayer ? selectedPaths.length : 1
        }`
      );
      if (saveMode === "folder") {
        outputDir = await pickOutputDirectory();
        if (!outputDir) {
          appendLog("未选择输出目录，已取消。");
          return;
        }
        appendLog(`输出目录: ${outputDir}`);
      }

      const targets = hasBatchLayer ? selectedPaths : ["__single__"];

      const tasks = targets.map((path) =>
        queue.add(async () => {
          if (cancelledRef.current) {
            return;
          }
          try {
            appendLog(`开始处理: ${path === "__single__" ? "fixed" : path}`);
            const composedLayers = await resolveComposedLayers(path === "__single__" ? null : path);
            const blob = await composeOutput(templateImage, composedLayers, exportFormat, exportQuality);
            const bytes = new Uint8Array(await blob.arrayBuffer());
            const outputNameBase = path === "__single__" ? "output-fixed" : baseNameFromPath(path);
            const outputName = `${sanitizeFileName(outputNameBase)}.${ext}`;
            const relativeDir = path === "__single__" ? "" : relativeDirectoryPath(path, recordsDir);
            const relativeOutputPath = relativeDir ? `${relativeDir}/${outputName}` : outputName;

            if (saveMode === "folder") {
              if (!outputDir) {
                throw new Error("输出目录为空");
              }
              await writeBytesToDirectory(outputDir, relativeOutputPath, bytes);
            } else {
              zip.file(relativeOutputPath, bytes);
            }
            appendLog(`输出完成: ${relativeOutputPath}`);
          } catch (err) {
            failed += 1;
            const msg = err instanceof Error ? err.message : "unknown error";
            appendLog(`失败: ${path === "__single__" ? "fixed" : fileNameFromPath(path)} - ${msg}`);
          } finally {
            done += 1;
            setProgress(Math.round((done / targets.length) * 100));
          }
        })
      );

      await Promise.all(tasks);

      if (cancelledRef.current) {
        appendLog("批量任务已取消");
        return;
      }

      if (saveMode === "zip") {
        const zipBytes = await zip.generateAsync({
          type: "uint8array",
          compression: "DEFLATE",
          compressionOptions: { level: 6 }
        });
        const savePath = await saveBytes(`outputs-${ts()}.zip`, zipBytes);
        if (savePath) {
          appendLog(`ZIP 已保存: ${savePath}`);
        } else {
          appendLog("ZIP 保存已取消");
        }
      } else {
        appendLog("已保存到指定输出目录。");
      }

      appendLog(`完成: 总数=${targets.length}, 失败=${failed}`);
    } finally {
      queueRef.current = null;
      setRunning(false);
    }
  }

  function onCancelBatch() {
    cancelledRef.current = true;
    queueRef.current?.clear();
  }

  return (
    <main className="shad-shell">
      <header className="hero">
        <div>
          <p className="eyebrow">Batch Composer</p>
          <h1>图片批量合成工作台</h1>
          <p className="subtitle">多图层 + 透视变形 + 首张确认 + 批量导出（ZIP 或目录）。</p>
        </div>
        <div className="step-grid">
          <StepBadge index={1} title="模板图" done={stepDone.step1} />
          <StepBadge index={2} title="多图层定位" done={stepDone.step2} />
          <StepBadge index={3} title="素材选择" done={stepDone.step3} />
          <StepBadge index={4} title="预览确认" done={stepDone.step4} />
        </div>
      </header>

      <section className="content-grid">
        <div className="left-pane">
          <article className="card">
            <h2>1. 上传模板与图层</h2>
            <div className="uploader-grid">
              <div className={`dropzone ${templateDrop.isDragActive ? "drag-active" : ""}`} {...templateDrop.getRootProps()}>
                <input {...templateDrop.getInputProps()} />
                <p>{templateName || "上传模板图"}</p>
              </div>
              <div className={`dropzone ${layerDrop.isDragActive ? "drag-active" : ""}`} {...layerDrop.getRootProps()}>
                <input {...layerDrop.getInputProps()} />
                <p>批量上传图层（可多选）</p>
              </div>
            </div>
          </article>

          <article className="card">
            <h2>2. 图层编辑（拖拽 / 旋转 / 透视）</h2>
            {!templateImage ? (
              <p className="empty-state">先上传模板图后可开始定位。</p>
            ) : (
              <div className="editor-wrap">
                <div className="editor-canvas" ref={editorRef} style={{ width: stageWidth, height: stageHeight }}>
                  <img
                    className="editor-template"
                    src={templateImage.src}
                    alt="模板图"
                    width={stageWidth}
                    height={stageHeight}
                    draggable={false}
                  />
                  {layers.map((layer) => {
                    if (!layer.visible) {
                      return null;
                    }
                    const isActive = layer.id === activeLayerId;
                    const displayPlacement = {
                      x: layer.geometry.placement.x * stageScale,
                      y: layer.geometry.placement.y * stageScale,
                      width: layer.geometry.placement.width * stageScale,
                      height: layer.geometry.placement.height * stageScale
                    };
                    return (
                      <div
                        key={layer.id}
                        ref={(el) => setLayerElementRef(layer.id, el)}
                        className={`editor-layer ${isActive ? "active" : ""} ${
                          layer.geometry.perspectiveEnabled ? "perspective" : "normal"
                        }`}
                        style={{
                          left: `${displayPlacement.x}px`,
                          top: `${displayPlacement.y}px`,
                          width: `${displayPlacement.width}px`,
                          height: `${displayPlacement.height}px`,
                          opacity: layer.opacity,
                          transformOrigin: "0 0",
                          transform: hasWarpShape(layer)
                            ? layer.editorTransform ?? IDENTITY_MATRIX3D
                            : `rotate(${layer.geometry.placement.rotation}deg)`
                        }}
                        data-layer-id={layer.id}
                        data-rotation={layer.geometry.placement.rotation}
                        onMouseDown={() => {
                          appendLog(`鼠标按下图层: ${layer.id}`);
                          setActiveLayerId(layer.id);
                        }}
                        onClick={() => {
                          appendLog(`鼠标点击图层: ${layer.id}`);
                        }}
                      >
                        <img src={layer.image.src} alt={layer.name} draggable={false} />
                      </div>
                    );
                  })}
                  {activeTarget && activeLayer ? (
                    <Moveable
                      target={activeTarget}
                      container={editorRef.current ?? undefined}
                      origin={false}
                      edge={false}
                      renderDirections={activeLayer.geometry.perspectiveEnabled ? (["nw", "ne", "sw", "se"] as const) : undefined}
                      draggable
                      resizable={!activeLayer.geometry.perspectiveEnabled}
                      rotatable={!activeLayer.geometry.perspectiveEnabled && !hasWarpShape(activeLayer)}
                      warpable={activeLayer.geometry.perspectiveEnabled}
                      keepRatio={false}
                      throttleDrag={0}
                      throttleResize={0}
                      throttleRotate={0}
                      onDragStart={() => {
                        moveableDragMovedRef.current = 0;
                        moveableDragTickRef.current = 0;
                        trace(
                          `${activeLayer.geometry.perspectiveEnabled || hasWarpShape(activeLayer) ? "透视形状" : "普通"}拖拽开始: ${
                            activeLayer.id
                          }`
                        );
                      }}
                      onDrag={({ target, left, top, beforeDelta }) => {
                        if (Array.isArray(beforeDelta)) {
                          moveableDragMovedRef.current += Math.abs(beforeDelta[0] ?? 0) + Math.abs(beforeDelta[1] ?? 0);
                        }
                        target.style.left = `${left}px`;
                        target.style.top = `${top}px`;
                        moveableDragTickRef.current += 1;
                        if (moveableDragTickRef.current % 8 === 0) {
                          trace(
                            `${activeLayer.geometry.perspectiveEnabled || hasWarpShape(activeLayer) ? "透视形状" : "普通"}拖拽中: ${
                              activeLayer.id
                            } left=${rounded(
                              left
                            )} top=${rounded(top)} moved=${rounded(moveableDragMovedRef.current)}`
                          );
                        }
                      }}
                      onDragEnd={({ target }) => {
                        if (!target || !(target instanceof HTMLDivElement)) {
                          return;
                        }
                        if (activeLayer.geometry.perspectiveEnabled || hasWarpShape(activeLayer)) {
                          if (moveableDragMovedRef.current < 1) {
                            appendLog(`透视拖拽忽略(点击阈值): ${activeLayer.id}`);
                            return;
                          }
                          commitPerspectiveFromElement(activeLayer.id, target, "drag");
                          return;
                        }
                        commitNormalFromElement(activeLayer.id, target, "drag");
                      }}
                      onResizeStart={({ target }) => {
                        if (target instanceof HTMLDivElement) {
                          target.dataset.resizeStartLeft = `${px(target.style.left)}`;
                          target.dataset.resizeStartTop = `${px(target.style.top)}`;
                        }
                        trace(
                          `${activeLayer.geometry.perspectiveEnabled || hasWarpShape(activeLayer) ? "透视形状" : "普通"}缩放开始: ${
                            activeLayer.id
                          }`
                        );
                      }}
                      onResize={({ target, width, height, drag }) => {
                        target.style.width = `${Math.max(10, width)}px`;
                        target.style.height = `${Math.max(10, height)}px`;
                        const [dx, dy] = drag.beforeTranslate;
                        const baseLeft = px(target.dataset.resizeStartLeft, px(target.style.left));
                        const baseTop = px(target.dataset.resizeStartTop, px(target.style.top));
                        target.style.left = `${baseLeft + dx}px`;
                        target.style.top = `${baseTop + dy}px`;
                      }}
                      onResizeEnd={({ target }) => {
                        if (!target || !(target instanceof HTMLDivElement)) {
                          return;
                        }
                        if (activeLayer.geometry.perspectiveEnabled || hasWarpShape(activeLayer)) {
                          commitPerspectiveFromElement(activeLayer.id, target, "resize");
                          return;
                        }
                        commitNormalFromElement(activeLayer.id, target, "resize");
                      }}
                      onRotateStart={() => {
                        appendLog(`普通旋转开始: ${activeLayer.id}`);
                      }}
                      onRotate={({ target, beforeRotate }) => {
                        target.style.transform = `rotate(${beforeRotate}deg)`;
                        target.dataset.rotation = `${beforeRotate}`;
                      }}
                      onRotateEnd={({ target }) => {
                        if (!target || !(target instanceof HTMLDivElement)) {
                          return;
                        }
                        commitNormalFromElement(activeLayer.id, target, "rotate");
                      }}
                      onWarpStart={({ target }) => {
                        if (!activeLayer.geometry.perspectiveEnabled) {
                          return;
                        }
                        moveableWarpTickRef.current = 0;
                        trace(`${new Date().toISOString()} 透视变形开始: ${activeLayer.id}`);
                        target.style.transform = target.style.transform || activeLayer.editorTransform || IDENTITY_MATRIX3D;
                      }}
                      onWarp={({ target, transform }) => {
                        target.style.transform = transform;
                        moveableWarpTickRef.current += 1;
                        if (moveableWarpTickRef.current % 6 === 0) {
                          trace(`${new Date().toISOString()} 透视变形中: ${activeLayer.id} matrix=${transform.slice(0, 64)}...`);
                        }
                      }}
                      onWarpEnd={({ target }) => {
                        if (!target || !(target instanceof HTMLDivElement)) {
                          return;
                        }
                        commitPerspectiveFromElement(activeLayer.id, target, "warp");
                      }}
                    />
                  ) : null}
                </div>
              </div>
            )}
          </article>
        </div>

        <aside className="right-pane">
          <article className="card">
            <h2>图层面板</h2>
            <div className="layer-list">
              {layers.map((layer) => (
                <div key={layer.id} className={`layer-item ${layer.id === activeLayerId ? "active" : ""}`} onClick={() => setActiveLayerId(layer.id)}>
                  <div className="layer-top">
                    <strong>{layer.name}</strong>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeLayer(layer.id);
                      }}
                    >
                      删除
                    </button>
                  </div>
                  <div className="layer-controls">
                    <label>
                      <input
                        type="checkbox"
                        checked={layer.visible}
                        onChange={(event) => updateLayer(layer.id, (prev) => ({ ...prev, visible: event.target.checked }))}
                      />
                      显示
                    </label>
                    <label>
                      来源
                      <select
                        value={layer.sourceMode}
                        onChange={(event) => setLayerSourceMode(layer.id, event.target.value as LayerSourceMode)}
                      >
                        <option value="fixed">固定图层</option>
                        <option value="batch">批处理图层</option>
                      </select>
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={layer.geometry.perspectiveEnabled}
                        onChange={(event) => togglePerspective(layer.id, event.target.checked)}
                      />
                      透视
                    </label>
                    <label className="opacity-control">
                      透明度 {Math.round(layer.opacity * 100)}%
                      <input
                        type="range"
                        min={0.05}
                        max={1}
                        step={0.01}
                        value={layer.opacity}
                        onChange={(event) => {
                          const next = Number(event.target.value);
                          updateLayer(layer.id, (prev) => ({ ...prev, opacity: next }), { clearPreviewNow: false });
                          appendLog(`透明度调整: ${layer.id} -> ${Math.round(next * 100)}%`);
                        }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        resetLayerPlacement(layer.id);
                      }}
                    >
                      重置位置
                    </button>
                    {layer.geometry.perspectiveEnabled ? (
                      <button
                        type="button"
                        className="btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          resetPerspective(layer.id);
                        }}
                      >
                        重置透视
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="card">
            <h2>3. 素材选择</h2>
            <div className="button-row">
              <button className="btn btn-primary" onClick={onPickRecordsFolder} disabled={running || pickingRecordsDir}>
                {pickingRecordsDir ? "选择中..." : "选择文件夹"}
              </button>
              <button className="btn" onClick={() => selectAllFiles(true)} disabled={running || allImagePaths.length === 0}>
                全选
              </button>
              <button className="btn" onClick={() => selectAllFiles(false)} disabled={running || allImagePaths.length === 0}>
                全不选
              </button>
            </div>
            <p className="meta-line truncate">{recordsDir || "未选择目录"}</p>
            <p className="meta-line">
              共 {allImagePaths.length} 张，已选 {selectedPaths.length} 张
            </p>
            <p className="meta-line">导出时会保留该目录下的子文件夹结构。</p>
            <div className="file-list">
              {allImagePaths.map((path) => (
                <label key={path} className="file-row">
                  <input type="checkbox" checked={Boolean(selectedMap[path])} onChange={(e) => togglePath(path, e.target.checked)} />
                  <span>{fileNameFromPath(path)}</span>
                </label>
              ))}
            </div>
          </article>

          <article className="card">
            <h2>4. 导出设置与执行</h2>
            <div className="form-grid">
              <label>
                导出格式
                <select
                  value={exportFormat}
                  onChange={(event) => {
                    const next = event.target.value as ExportFormat;
                    setExportFormat(next);
                    markDirty();
                    appendLog(`导出格式切换: ${next}`);
                  }}
                >
                  <option value="png">PNG</option>
                  <option value="jpeg">JPEG</option>
                  <option value="webp">WEBP</option>
                </select>
              </label>
              <label>
                保存方式
                <select
                  value={saveMode}
                  onChange={(event) => {
                    const next = event.target.value as SaveMode;
                    setSaveMode(next);
                    appendLog(`保存方式切换: ${next}`);
                  }}
                >
                  <option value="zip">ZIP 压缩包</option>
                  <option value="folder">保存到文件夹</option>
                </select>
              </label>
              <label>
                图像质量 ({Math.round(exportQuality * 100)}%)
                <input
                  type="range"
                  min={0.5}
                  max={1}
                  step={0.01}
                  value={exportQuality}
                  onChange={(event) => {
                    setExportQuality(Number(event.target.value));
                    markDirty();
                  }}
                />
              </label>
            </div>

            <div className="button-row">
              <button className="btn btn-primary" onClick={onGenerateFirstPreview} disabled={!canPreview}>
                生成首张预览
              </button>
              <button className="btn btn-primary" onClick={onGenerateBatch} disabled={!canBatch}>
                批量导出
              </button>
              <button className="btn" onClick={onCancelBatch} disabled={!running}>
                取消
              </button>
            </div>
            <p className={`meta-line ${layoutDirty ? "warning" : "ok"}`}>
              {layoutDirty ? "图层位置有改动，请先重新预览再批量导出。" : "预览已确认，可开始批量导出。"}
            </p>

            <div className="progress-wrap" aria-live="polite">
              <div className="progress-label">
                <span>任务进度</span>
                <strong>{progress}%</strong>
              </div>
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {previewUrl ? (
              <div className="preview-inline-actions">
                <p className="meta-line">首张预览已生成：{previewSourceName}</p>
                <button className="btn" type="button" onClick={() => setPreviewModalOpen(true)}>
                  查看预览
                </button>
              </div>
            ) : (
              <p className="empty-state">尚未生成预览。</p>
            )}
          </article>
        </aside>
      </section>

      <section className="card log-card">
        <h2>任务日志</h2>
        <pre className="log-box">{logs.length ? logs.join("\n") : "暂无日志"}</pre>
      </section>

      {previewUrl && previewModalOpen ? (
        <div
          className="preview-modal-backdrop"
          role="presentation"
          onClick={() => {
            setPreviewModalOpen(false);
          }}
        >
          <div
            className="preview-modal"
            role="dialog"
            aria-modal="true"
            aria-label="首张预览"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="preview-modal-header">
              <h3>首张预览（{previewSourceName}）</h3>
              <button type="button" className="btn" onClick={() => setPreviewModalOpen(false)}>
                关闭
              </button>
            </div>
            <img src={previewUrl} alt="首张预览结果" loading="lazy" />
          </div>
        </div>
      ) : null}
    </main>
  );
}

export default App;
